import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

export const JornadasProcesoGerente = () => {
  // =========================
  // Contexto / hooks globales
  // =========================
  const { auth } = useAuth();
  const toast = useToast();

  // =========================
  // Estado
  // =========================
  const [jornadas, setJornadas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  // =========================
  // Carga inicial de datos
  // =========================
  useEffect(() => {
    if (!auth?.id) return;
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.id]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [resJornadas, resUsuarios] = await Promise.all([
        fetch(`${Global.url}jornadas/proceso`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}usuarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      if (!resJornadas.ok) {
        toast.error("No se pudieron obtener las jornadas en proceso.");
        setJornadas([]);
      } else {
        const data = await resJornadas.json();
        setJornadas(Array.isArray(data) ? data : []);
      }

      if (!resUsuarios.ok) {
        toast.warning("No se pudieron cargar los usuarios.");
        setUsuarios([]);
      } else {
        const data = await resUsuarios.json();
        setUsuarios(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      toast.error("Error de conexión con el servidor.");
      setJornadas([]);
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Helpers
  // =========================
  const getNombreCompleto = (usuario_id) => {
    const u = usuarios.find((x) => String(x.id) === String(usuario_id));
    return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : `ID ${usuario_id ?? "-"}`;
  };

  const fmtDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // =========================
  // Filtrado: solo mi equipo
  // =========================
  const jornadasFiltradas = useMemo(() => {
    if (!auth?.id) return [];

    const idsEquipo = new Set(
      usuarios
        .filter((u) => String(u.gerente_id) === String(auth.id))
        .map((u) => String(u.id))
    );

    return jornadas.filter((j) => idsEquipo.has(String(j.usuario_id)));
  }, [jornadas, usuarios, auth?.id]);

  // =========================
  // Preparación DataGrid
  // =========================
  const rows = useMemo(
    () =>
      jornadasFiltradas.map((j) => ({
        ...j,
        usuario_nombre: getNombreCompleto(j.usuario_id),
        inicio_txt: fmtDate(j.fecha_inicio),
        comentarios_txt: j.comentarios || "Sin comentarios",
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jornadasFiltradas, usuarios]
  );

  const columns = useMemo(
    () => [
      { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 200 },
      { field: "centro", headerName: "Centro", flex: 1, minWidth: 220 },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 180 },
      { field: "comentarios_txt", headerName: "Comentarios", flex: 1.5, minWidth: 260 },
    ],
    []
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Jornadas en Proceso
        </Typography>

        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : "No hay jornadas activas actualmente."}
                </Box>
              ),
            }}
          />

        </Paper>
      </Stack>
    </Box>
  );
};
